import { useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { drumLanguageAtom, drumUserPreferencesAtom, drumUserTokenAtom } from '../../shared/store/drumAppAtoms';
import { saveMemberPreferences } from '../../shared/api/memberApi';
import './UserSettings.scss';

function UserSettings() {
  const [language, setLanguage] = useAtom(drumLanguageAtom);
  const token = useAtomValue(drumUserTokenAtom);
  const [preferences, setPreferences] = useAtom(drumUserPreferencesAtom);
  const [errorMessage, setErrorMessage] = useState('');

  const persistPreferences = async (next: typeof preferences) => {
    const previous = preferences;
    setPreferences(next);
    setErrorMessage('');
    if (!token) return;
    try {
      const saved = await saveMemberPreferences(token, next);
      setPreferences(saved);
    } catch (error) {
      setPreferences(previous);
      setLanguage(previous.language);
      setErrorMessage(error instanceof Error ? error.message : (language === 'ko' ? '설정을 저장하지 못했습니다.' : 'Unable to save changes.'));
    }
  };

  const changeLanguage = (nextLanguage: string) => {
    setLanguage(nextLanguage);
    persistPreferences({ ...preferences, language: nextLanguage });
  };

  const toggles: Array<[string, boolean, () => void]> = [
    ['Auto-play song preview', preferences.autoPreview, () => persistPreferences({ ...preferences, autoPreview: !preferences.autoPreview })],
    ['Judgment sound effects', preferences.judgmentSound, () => persistPreferences({ ...preferences, judgmentSound: !preferences.judgmentSound })],
  ];

  return (
    <div className="page settings-page">
      <div className="page-intro"><span className="eyebrow">Settings</span><h1>App settings</h1><p>Manage language, sound and play preferences.</p></div>
      <section className="settings-section">
        <div>
          <h2>Language</h2>
          <p>Choose the language used for menus, controls and learning feedback.</p>
          <small>Song titles and artist names stay in their original language.</small>
        </div>
        <div className="language-control">
          <button type="button" aria-pressed={language === 'en'} className={language === 'en' ? 'active' : ''} onClick={() => changeLanguage('en')}>English</button>
          <button type="button" aria-pressed={language === 'ko'} className={language === 'ko' ? 'active' : ''} onClick={() => changeLanguage('ko')}>한국어</button>
        </div>
      </section>
      <section className="settings-section stacked">
        <div><h2>Sound & Play</h2></div>
        {toggles.map(([label, value, onToggle]) => (
          <button type="button" role="switch" aria-checked={value} className="setting-toggle" key={label} onClick={onToggle}>
            <span>{label}</span><i className={value ? 'on' : ''} aria-hidden="true"><b /></i>
          </button>
        ))}
      </section>
      {errorMessage ? <p className="profile-form-status">{errorMessage}</p> : null}
    </div>
  );
}

export default UserSettings;
